// =============================================================================
// JOURNEY SERVICE - Firebase CRUD for Journey Entries
// Journey = reflectie, herinnering, chronologisch (GEEN voortgang, GEEN analyse)
// =============================================================================

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { JourneyEntry, JourneyEntryType } from '../types';

const COLLECTION_NAME = 'journeyEntries';

// Minimum milliseconds between weekly reflections (7 days)
const REFLECTION_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

// -----------------------------------------------------------------------------
// INTERNAL HELPERS
// -----------------------------------------------------------------------------

/**
 * Check if a specific entry type exists for a goal
 */
async function hasEntryOfType(
  ownerId: string,
  goalId: string,
  type: JourneyEntryType
): Promise<boolean> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId),
    where('type', '==', type),
    limit(1)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Get existing entry of type (for returning existing instead of creating duplicate)
 */
async function getEntryOfType(
  ownerId: string,
  goalId: string,
  type: JourneyEntryType
): Promise<JourneyEntry | null> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId),
    where('type', '==', type),
    limit(1)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as JourneyEntry;
}

// -----------------------------------------------------------------------------
// CREATE (INTERNAL)
// -----------------------------------------------------------------------------

/**
 * Create a new journey entry (internal, no guards)
 */
async function createJourneyEntryInternal(
  ownerId: string,
  goalId: string,
  type: JourneyEntryType,
  data?: {
    milestoneId?: string;
    videoUrl?: string;
    textContent?: string;
  }
): Promise<string> {
  const entryData: Omit<JourneyEntry, 'id'> = {
    ownerId,
    goalId,
    type,
    createdAt: Date.now(),
    ...(data?.milestoneId && { milestoneId: data.milestoneId }),
    ...(data?.videoUrl && { videoUrl: data.videoUrl }),
    ...(data?.textContent && { textContent: data.textContent }),
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), entryData);
  
  // Update the document with its own ID for consistency
  await updateDoc(docRef, { id: docRef.id });
  
  return docRef.id;
}

// -----------------------------------------------------------------------------
// CREATE (PUBLIC - WITH GUARDS)
// -----------------------------------------------------------------------------

/**
 * Create the initial "start" entry for a goal journey
 * - Only ONE start per goal (returns existing if already exists)
 * - Called when a goal is committed
 */
export async function createJourneyStart(
  ownerId: string,
  goalId: string,
  videoUrl?: string
): Promise<string> {
  // Check for existing start entry
  const existing = await getEntryOfType(ownerId, goalId, 'start');
  if (existing) {
    return existing.id; // Return existing, no duplicate
  }
  
  return createJourneyEntryInternal(ownerId, goalId, 'start', { videoUrl });
}

/**
 * Create a reflection entry
 * - NOT allowed if journey is closed
 * - Caller should check canCreateWeeklyReflection() first
 */
export async function createReflection(
  ownerId: string,
  goalId: string,
  textContent: string,
  videoUrl?: string
): Promise<string | null> {
  // Guard: journey must be open
  const closed = await isJourneyClosed(ownerId, goalId);
  if (closed) {
    return null; // Journey closed, no new reflections
  }
  
  return createJourneyEntryInternal(ownerId, goalId, 'reflection', { textContent, videoUrl });
}

/**
 * Create a milestone entry (when milestone is added to journey)
 * - NOT allowed if journey is closed
 */
export async function createMilestoneEntry(
  ownerId: string,
  goalId: string,
  milestoneId: string,
  textContent?: string
): Promise<string | null> {
  // Guard: journey must be open
  const closed = await isJourneyClosed(ownerId, goalId);
  if (closed) {
    return null; // Journey closed, no new milestone entries
  }
  
  return createJourneyEntryInternal(ownerId, goalId, 'milestone', { milestoneId, textContent });
}

/**
 * Create the closing entry for a goal journey (internal)
 * - Only ONE closing per goal
 * - Must have a start entry
 */
async function createJourneyClosingInternal(
  ownerId: string,
  goalId: string,
  textContent?: string,
  videoUrl?: string
): Promise<string | null> {
  // Guard: must have start
  const hasStart = await hasEntryOfType(ownerId, goalId, 'start');
  if (!hasStart) {
    return null; // No start, cannot close
  }
  
  // Guard: not already closed
  const alreadyClosed = await hasEntryOfType(ownerId, goalId, 'closing');
  if (alreadyClosed) {
    return null; // Already closed
  }
  
  return createJourneyEntryInternal(ownerId, goalId, 'closing', { textContent, videoUrl });
}

// -----------------------------------------------------------------------------
// READ
// -----------------------------------------------------------------------------

/**
 * Get all journey entries for a goal, chronologically ordered
 * @param ownerId - Firebase Auth UID
 * @param goalId - Goal ID
 * @returns Array of JourneyEntry objects (oldest first)
 */
export async function getJourneyEntriesByGoal(
  ownerId: string,
  goalId: string
): Promise<JourneyEntry[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId),
    orderBy('createdAt', 'asc')
  );

  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as JourneyEntry));
}

/**
 * Get all journey entries for a user (across all goals)
 * @param ownerId - Firebase Auth UID
 * @returns Array of JourneyEntry objects
 */
export async function getAllJourneyEntries(ownerId: string): Promise<JourneyEntry[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    orderBy('createdAt', 'asc')
  );

  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as JourneyEntry));
}

/**
 * Get the most recent reflection for a goal
 */
export async function getLastReflection(
  ownerId: string,
  goalId: string
): Promise<JourneyEntry | null> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId),
    where('type', '==', 'reflection'),
    orderBy('createdAt', 'desc'),
    limit(1)
  );

  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data(),
  } as JourneyEntry;
}

// -----------------------------------------------------------------------------
// VALIDATION
// -----------------------------------------------------------------------------

/**
 * Check if journey has been closed
 */
export async function isJourneyClosed(
  ownerId: string,
  goalId: string
): Promise<boolean> {
  return hasEntryOfType(ownerId, goalId, 'closing');
}

/**
 * Check if journey has started
 */
export async function hasJourneyStarted(
  ownerId: string,
  goalId: string
): Promise<boolean> {
  return hasEntryOfType(ownerId, goalId, 'start');
}

/**
 * Check if user can create a new weekly reflection
 * Returns true if:
 * - Journey is NOT closed
 * - >= 7 days since last reflection (or no reflection exists)
 */
export async function canCreateWeeklyReflection(
  ownerId: string,
  goalId: string
): Promise<boolean> {
  // Guard: journey must be open
  const closed = await isJourneyClosed(ownerId, goalId);
  if (closed) {
    return false; // Journey closed, no reflections allowed
  }
  
  const lastReflection = await getLastReflection(ownerId, goalId);
  
  if (!lastReflection) {
    return true; // No previous reflection, allowed
  }
  
  // Use milliseconds for precision (timezone-independent)
  const msSinceLastReflection = Date.now() - lastReflection.createdAt;
  
  return msSinceLastReflection >= REFLECTION_INTERVAL_MS;
}

/**
 * Get days until next reflection is allowed
 * Returns 0 if reflection is allowed now
 * Returns -1 if journey is closed (never allowed)
 */
export async function getDaysUntilNextReflection(
  ownerId: string,
  goalId: string
): Promise<number> {
  // Guard: journey must be open
  const closed = await isJourneyClosed(ownerId, goalId);
  if (closed) {
    return -1; // Journey closed, never allowed
  }
  
  const lastReflection = await getLastReflection(ownerId, goalId);
  
  if (!lastReflection) {
    return 0; // No previous reflection, allowed now
  }
  
  const msSinceLastReflection = Date.now() - lastReflection.createdAt;
  const msRemaining = REFLECTION_INTERVAL_MS - msSinceLastReflection;
  
  if (msRemaining <= 0) {
    return 0;
  }
  
  // Convert to days, round up
  return Math.ceil(msRemaining / (24 * 60 * 60 * 1000));
}

// -----------------------------------------------------------------------------
// UPDATE
// -----------------------------------------------------------------------------

/**
 * Update an existing journey entry
 * Only textContent and videoUrl can be updated
 */
export async function updateJourneyEntry(
  entryId: string,
  data: {
    textContent?: string;
    videoUrl?: string;
  }
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, entryId);
  await updateDoc(docRef, data);
}

// -----------------------------------------------------------------------------
// DELETE
// -----------------------------------------------------------------------------

/**
 * Delete a journey entry
 * Note: Use with caution, journey entries are meant to be permanent
 */
export async function deleteJourneyEntry(entryId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, entryId);
  await deleteDoc(docRef);
}

// -----------------------------------------------------------------------------
// CLOSE JOURNEY
// -----------------------------------------------------------------------------

/**
 * Close the journey for a goal
 * - Creates a closing entry if one doesn't exist
 * - Requires start entry to exist
 * - Video and text are optional
 * - Milestones are NOT required
 */
export async function closeJourney(
  ownerId: string,
  goalId: string,
  textContent?: string,
  videoUrl?: string
): Promise<string | null> {
  return createJourneyClosingInternal(ownerId, goalId, textContent, videoUrl);
}
